import React, { useCallback } from 'react';
import {
  Typography,
  Box,
  Grid,
  TextField,
  InputAdornment,
  CircularProgress,
  Button,
  Alert,
  Checkbox,
  Chip,
  FormControl,
  InputLabel,
  ListItemText,
  Select,
  MenuItem,
  SelectChangeEvent,
  Paper,
  Stack,
  Divider,
} from '@mui/material';
import {
  Search,
  Inventory2 as InventoryIcon,
} from '@mui/icons-material';
import { Lead } from '@otw/models/leads/lead';
import { Vehicle } from '@otw/models/inventory/inventory';
import { vehicleStatusOptions } from '@/constants/inventory';
import { useInventory } from 'hooks/inventory/useInventory';
import { VehicleCard } from '../../cards';
import { useVehicleAttachment } from '../../../contexts';
import { useSimilarityCalculation } from 'hooks/useSimilarityCalculation';
import { useAuth } from 'hooks/auth/useAuth';

interface InventoryTabProps {
  lead: Lead;
}

const InventoryTab: React.FC<InventoryTabProps> = ({ lead }) => {
  const { isLoading } = useInventory("master_inventory");
  const { userInfo } = useAuth();
  const { calculateVehicleSimilarity } = useSimilarityCalculation();

  const {
    attachedVehicleIds,
    attachingVehicle,
    handleAttachVehicleToLead,
    handleRemoveVehicleFromLead,
    searchQuery,
    setSearchQuery,
    searchPerformed,
    setSearchPerformed,
    filterType,
    setFilterType,
    filterStatus,
    setFilterStatus,
    searchResults,
    handleInventorySearch,
  } = useVehicleAttachment();

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };

  const handleFilterTypeChange = (event: SelectChangeEvent) => {
    setFilterType(event.target.value);
  };

  const handleFilterStatusChange = (event: SelectChangeEvent<string[]>) => {
    const rawValue = event.target.value;
    const next = typeof rawValue === 'string' ? rawValue.split(',') : rawValue;
    const previouslyAll = filterStatus.includes('all') || filterStatus.length === 0;
    const clickedAll = next.includes('all') && !previouslyAll;
    if (clickedAll || next.length === 0) {
      setFilterStatus(['all']);
      return;
    }
    setFilterStatus(next.filter((status) => status !== 'all'));
  };

  const selectedStatusLabels = filterStatus.includes('all') || filterStatus.length === 0
    ? ['All statuses']
    : filterStatus;

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleInventorySearch();
    }
  };

  const handleAttachVehicle = useCallback(
    (vehicle: Vehicle) => {
      handleAttachVehicleToLead(vehicle);
    },
    [handleAttachVehicleToLead],
  );

  const handleRemoveVehicle = useCallback(
    (vehicle: Vehicle) => {
      handleRemoveVehicleFromLead(vehicle);
    },
    [handleRemoveVehicleFromLead],
  );

  const processedSearchResults = searchResults.map(vehicle =>
    calculateVehicleSimilarity(vehicle, lead),
  );

  return (
    <Stack spacing={3}>
      <Paper variant='outlined' sx={{ p: { xs: 2, md: 2.5 }, borderRadius: 3 }}>
        <Stack direction='row' alignItems='center' spacing={1.5} sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', color: 'primary.main' }}>
            <InventoryIcon fontSize='small' />
          </Box>
          <Typography
            variant='caption'
            sx={{
              textTransform: 'uppercase',
              fontWeight: 700,
              letterSpacing: 1.1,
              color: 'text.secondary',
            }}
          >
            Search inventory
          </Typography>
          <Divider sx={{ flexGrow: 1 }} />
        </Stack>

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1.5}
          alignItems={{ xs: 'stretch', sm: 'center' }}
        >
          <TextField
            fullWidth
            placeholder='Search by VIN, stock #, make, model, who, etc.'
            value={searchQuery}
            onChange={handleSearchChange}
            onKeyPress={handleKeyPress}
            InputProps={{
              startAdornment: (
                <InputAdornment position='start'>
                  <Search fontSize='small' />
                </InputAdornment>
              ),
            }}
            variant='outlined'
            size='small'
          />
          <FormControl variant='outlined' size='small' sx={{ minWidth: { xs: '100%', sm: 160 } }}>
            <InputLabel id='filter-type-label'>Type</InputLabel>
            <Select
              labelId='filter-type-label'
              value={filterType}
              onChange={handleFilterTypeChange}
              label='Type'
            >
              <MenuItem value='all'>All types</MenuItem>
              <MenuItem value='box truck'>Box truck</MenuItem>
              <MenuItem value='cargo van'>Cargo van</MenuItem>
              <MenuItem value='cutaway'>Cutaway</MenuItem>
              <MenuItem value='step van'>Step van</MenuItem>
            </Select>
          </FormControl>
          <FormControl variant='outlined' size='small' sx={{ minWidth: { xs: '100%', sm: 220 } }}>
            <InputLabel id='filter-status-label'>Status</InputLabel>
            <Select
              labelId='filter-status-label'
              multiple
              value={filterStatus.length === 0 ? ['all'] : filterStatus}
              onChange={handleFilterStatusChange}
              label='Status'
              renderValue={() => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selectedStatusLabels.map((status) => (
                    <Chip key={status} size='small' label={status} />
                  ))}
                </Box>
              )}
            >
              <MenuItem value='all'>
                <Checkbox
                  checked={filterStatus.includes('all') || filterStatus.length === 0}
                  size='small'
                />
                <ListItemText primary='All statuses' />
              </MenuItem>
              {vehicleStatusOptions.map((status) => (
                <MenuItem key={status} value={status}>
                  <Checkbox checked={filterStatus.includes(status)} size='small' />
                  <ListItemText primary={status} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant='contained'
            onClick={handleInventorySearch}
            startIcon={<Search />}
            sx={{ minWidth: { xs: '100%', sm: 120 } }}
          >
            Search
          </Button>
        </Stack>
      </Paper>

      {isLoading ? (
        <Box display='flex' justifyContent='center' alignItems='center' minHeight='200px'>
          <CircularProgress />
        </Box>
      ) : searchPerformed ? (
        processedSearchResults.length > 0 ? (
          <Stack spacing={2}>
            <Stack direction='row' alignItems='center' spacing={1}>
              <Typography variant='subtitle2' fontWeight={700}>
                {processedSearchResults.length}
              </Typography>
              <Typography variant='body2' color='text.secondary'>
                vehicles found
              </Typography>
            </Stack>
            <Grid container spacing={2}>
              {processedSearchResults.map(vehicle => (
                <Grid item xs={12} sm={6} md={4} key={vehicle.id}>
                  <VehicleCard
                    vehicle={vehicle}
                    attachedVehicleIds={attachedVehicleIds}
                    attachingVehicle={attachingVehicle}
                    onAttach={handleAttachVehicle}
                    onRemove={handleRemoveVehicle}
                    userRole={userInfo?.role}
                  />
                </Grid>
              ))}
            </Grid>
          </Stack>
        ) : (
          <Alert severity='info' sx={{ borderRadius: 2 }}>
            No vehicles found matching your search criteria. Try adjusting your search terms.
          </Alert>
        )
      ) : (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 280,
            p: 4,
            border: 1,
            borderStyle: 'dashed',
            borderColor: 'divider',
            borderRadius: 3,
            color: 'text.secondary',
          }}
        >
          <Search sx={{ fontSize: 56, opacity: 0.4, mb: 1.5 }} />
          <Typography variant='subtitle1' fontWeight={600} align='center'>
            Search the inventory to find and attach vehicles
          </Typography>
          <Typography variant='caption' color='text.secondary' align='center' sx={{ mt: 0.5 }}>
            Enter a search term like VIN, stock number, or vehicle details
          </Typography>
        </Box>
      )}
    </Stack>
  );
};

export default InventoryTab;
